import { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Slider
} from '@mui/material';
import Cropper from 'react-easy-crop';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import { getCroppedImg } from '../../../pages/settings/getCroppedImg';

interface ImageCropModalProps {
    open: boolean;
    imageSrc: string | null;
    originalFile: File | null;
    onClose: () => void;
    onConfirm: (compressedFile: File) => void;
}

const ImageCropModal = ({
    open,
    imageSrc,
    originalFile,
    onClose,
    onConfirm
}: ImageCropModalProps) => {
    const { t } = useTranslation();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

    const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleConfirm = async () => {
        if (!imageSrc || !croppedAreaPixels || !originalFile) return;

        try {
            // Ritaglia l'immagine
            const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

            // Estrai l'estensione dal file originale
            const originalFileName = originalFile.name;
            const lastDotIndex = originalFileName.lastIndexOf('.');
            const fileExtension = lastDotIndex > 0
                ? originalFileName.substring(lastDotIndex)
                : '.jpg'; // Default a .jpg se non c'è estensione
            const fileNameWithoutExt = lastDotIndex > 0
                ? originalFileName.substring(0, lastDotIndex)
                : originalFileName || 'image';
            const newFileName = `${fileNameWithoutExt}${fileExtension}`;

            // Ridimensionamento e compressione (200x200)
            const options = {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 200,
                useWebWorker: true,
                fileType: originalFile.type,
            };

            const compressedFile = await imageCompression(
                new File([croppedBlob], newFileName, { type: originalFile.type }),
                options
            );

            // Assicurati che il file compresso mantenga l'estensione corretta
            const finalFileName = compressedFile.name.endsWith(fileExtension)
                ? compressedFile.name
                : `${fileNameWithoutExt}${fileExtension}`;

            const finalFile = new File([compressedFile], finalFileName, {
                type: originalFile.type,
                lastModified: Date.now()
            });

            onConfirm(finalFile);

            // Reset stati
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setCroppedAreaPixels(null);

        } catch (error) {
            console.error('Errore nel crop/compressione:', error);
        }
    };

    const handleCancel = () => {
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>{t('crop_image:title')}</DialogTitle>
            <DialogContent>
                <div style={{ position: 'relative', width: '100%', height: 400 }}>
                    <Cropper
                        image={imageSrc || ''}
                        crop={crop}
                        zoom={zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={false}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onCropComplete}
                    />
                </div>
                <Box sx={{ mt: 2 }}>
                    <Typography gutterBottom>Zoom</Typography>
                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(e, value) => setZoom(value as number)}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    sx={{
                        backgroundColor: 'trasparent', color: '#475467', border: '1px solid #667085'
                    }}
                    onClick={handleCancel}
                >
                    {t('common:cancel')}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                        backgroundColor: '#E9495A',
                        '&:hover': { backgroundColor: '#E6263B' }
                    }}
                >
                    {t('common:confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImageCropModal;